import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { PlusOutlined } from "@ant-design/icons";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { hasAnyPermission, hasPermission } from "../../utils/permissions";
import LedgerAccountModal from "./LedgerAccountModal";
import type { BankAccount, FinanceDepartment, FinanceParty, FinanceVoucher, GlAccount } from "../../types";

export type VoucherType = "income" | "expense";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", bankType: "cash" },
  { value: "bank_transfer", label: "Bank transfer", bankType: "bank" },
  { value: "card", label: "Card", bankType: "card" },
  { value: "credit", label: "On credit (pay later)", bankType: null },
];

const accountLabel = (a: GlAccount) => `${a.code} — ${a.name}`;

interface Props {
  open: boolean;
  initialType: VoucherType;
  /** When present the modal edits an existing draft instead of creating. */
  editing?: FinanceVoucher | null;
  onClose: () => void;
  onSaved: (voucher: FinanceVoucher, created: boolean) => void;
}

/**
 * Create / edit an income or expense voucher.
 *
 * Validation happens twice: here for the obvious required fields, and again
 * server-side (which also enforces the closed-day rule, header accounts and
 * the payment-method / bank-account pairing). Server errors are surfaced
 * verbatim so the operator sees the real reason.
 */
const VoucherFormModal: React.FC<Props> = ({ open, initialType, editing, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [parties, setParties] = useState<FinanceParty[]>([]);
  const [submitNow, setSubmitNow] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);

  const isEdit = !!editing;
  const voucherType = (Form.useWatch("voucher_type", form) || initialType) as VoucherType;
  const paymentMethod = Form.useWatch("payment_method", form) as string;
  const watchedAmount = Number(Form.useWatch("amount", form) || 0);
  const watchedTax = Number(Form.useWatch("tax", form) || 0);

  const canIncome = hasAnyPermission("finance:income");
  const canExpense = hasAnyPermission("finance:expense");
  const canSwitchType = canIncome && canExpense && !isEdit;
  const canCreateAccount = hasPermission("finance:accounts");

  const loadMaster = useCallback(async () => {
    try {
      const [acc, dep, bank, party] = await Promise.all([
        financeService.getAccounts(),
        financeService.getDepartments(),
        financeService.getBankAccounts(),
        financeService.getParties(),
      ]);
      setAccounts(acc);
      setDepartments(dep);
      setBanks(bank);
      setParties(party);
    } catch (e: any) {
      setError(e?.message || "Failed to load finance master data");
    }
  }, []);

  useEffect(() => {
    if (open) {
      setAccountModalOpen(false);
      loadMaster();
    }
  }, [open, loadMaster]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitNow(false);
    if (editing) {
      form.setFieldsValue({
        voucher_type: editing.voucher_type,
        business_date: dayjs(editing.business_date),
        department_id: editing.department_id,
        account_id: editing.account_id,
        description: editing.description,
        amount: editing.amount,
        tax: editing.tax,
        payment_method: editing.payment_method,
        bank_account_id: editing.bank_account_id,
        party: editing.party,
        reference_no: editing.reference_no,
        remarks: editing.remarks,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        voucher_type: initialType,
        business_date: dayjs(),
        payment_method: "cash",
      });
    }
  }, [open, editing, initialType, form]);

  /** Posting accounts offered for the current voucher type. */
  const accountOptions = useMemo(() => {
    const eligible = accounts.filter((a) => {
      if (!a.is_posting || !a.is_active) return false;
      if (voucherType === "income") return a.type === "income";
      // Expense may hit an expense account, or a 7xxx capital account
      // (equipment, vehicles, renovation) which stays out of the P&L.
      return a.type === "expense" || (a.type === "asset" && a.code.startsWith("7"));
    });

    const groups =
      voucherType === "income"
        ? [{ label: "Income", accounts: eligible }]
        : [
            { label: "Operating expense", accounts: eligible.filter((a) => a.type === "expense") },
            { label: "Capital (asset)", accounts: eligible.filter((a) => a.type === "asset") },
          ];

    return groups
      .filter((g) => g.accounts.length > 0)
      .map((g) => ({
        label: g.label,
        options: g.accounts.map((a) => ({ value: a.id, label: accountLabel(a) })),
      }));
  }, [accounts, voucherType]);

  const bankType = PAYMENT_METHODS.find((m) => m.value === paymentMethod)?.bankType ?? null;
  const bankOptions = useMemo(
    () =>
      banks
        .filter((b) => b.is_active && (!bankType || b.type === bankType))
        .map((b) => ({ value: b.id, label: `${b.name} (${b.account_code || b.type})` })),
    [banks, bankType],
  );

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    setError(null);

    const payload = {
      voucher_type: values.voucher_type,
      business_date: (values.business_date as Dayjs).format("YYYY-MM-DD"),
      department_id: values.department_id,
      account_id: values.account_id,
      description: values.description,
      party: values.party || null,
      reference_no: values.reference_no || null,
      amount: Number(values.amount),
      tax: Number(values.tax || 0),
      payment_method: values.payment_method,
      bank_account_id: values.payment_method === "credit" ? null : values.bank_account_id,
      remarks: values.remarks || null,
      ...(isEdit ? {} : { submit: submitNow }),
    };

    try {
      const saved = isEdit
        ? await financeService.updateVoucher(editing!.id, payload)
        : await financeService.createVoucher(payload as any);
      onSaved(saved, !isEdit);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to save the voucher");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? `Edit ${editing?.voucher_no}` : `New ${voucherType === "income" ? "Income" : "Expense"}`}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText={isEdit ? "Save changes" : submitNow ? "Create & submit" : "Create voucher"}
      width={760}
      destroyOnClose
    >
      {error && (
        <Alert type="error" showIcon title="Could not save" description={error} style={{ marginBottom: 16 }} />
      )}

      <Form form={form} layout="vertical" autoComplete="off">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="voucher_type"
              label="Type"
              rules={[{ required: true }]}
            >
              <Select
                disabled={!canSwitchType}
                options={[
                  { value: "income", label: "Income", disabled: !canIncome },
                  { value: "expense", label: "Expense", disabled: !canExpense },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="business_date"
              label="Business date"
              rules={[{ required: true, message: "Pick a business date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(d) => d.isAfter(dayjs(), "day")}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: "Pick a department" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Department"
                options={departments.map((d) => ({ value: d.id, label: d.name }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="account_id"
          label="Ledger account"
          rules={[{ required: true, message: "Pick an account" }]}
          extra={
            <Space size={8} wrap>
              <span>Header accounts cannot be posted to directly.</span>
              {canCreateAccount && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => setAccountModalOpen(true)}
                >
                  Create new ledger account
                </Button>
              )}
            </Space>
          }
        >
          <Select showSearch optionFilterProp="label" placeholder="Search by code or name" options={accountOptions} />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: "Describe what this voucher is for" },
            { max: 500, message: "500 characters maximum" },
          ]}
        >
          <Input.TextArea rows={2} placeholder="e.g. Weekly grocery purchase for the restaurant" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="amount"
              label="Amount"
              rules={[{ required: true, message: "Enter an amount" }]}
            >
              <InputNumber style={{ width: "100%" }} min={0.01} step={100} precision={2} placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="tax" label="Tax" initialValue={0}>
              <InputNumber style={{ width: "100%" }} min={0} step={10} precision={2} placeholder="0.00" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="payment_method"
              label="Payment method"
              rules={[{ required: true, message: "Pick a payment method" }]}
            >
              <Select options={PAYMENT_METHODS.map((m) => ({ value: m.value, label: m.label }))} />
            </Form.Item>
          </Col>
        </Row>

        {paymentMethod !== "credit" && (
          <Form.Item
            name="bank_account_id"
            label={`Receiving / paying ${bankType || "bank"} account`}
            rules={[{ required: true, message: "Pick the cash, bank or card account" }]}
          >
            <Select showSearch optionFilterProp="label" placeholder="Select account" options={bankOptions} />
          </Form.Item>
        )}

        <Row gutter={12}>
          <Col span={10}>
            <Form.Item
              name="party"
              label={paymentMethod === "credit" ? "Supplier / customer (required for credit)" : "Party"}
              rules={[
                {
                  required: paymentMethod === "credit",
                  message: "Credit entries need a party name",
                },
              ]}
            >
              <AutoComplete
                placeholder="Who is this with?"
                options={parties.map((p) => ({ value: p.name }))}
                filterOption={(input, option) =>
                  String(option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item name="reference_no" label="Reference / invoice no.">
              <Input placeholder="INV-001" />
            </Form.Item>
          </Col>
          <Col span={7}>
            <Form.Item label="Total">
              <Input disabled value={formatLkr(watchedAmount + watchedTax)} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="remarks" label="Internal remarks">
          <Input.TextArea rows={2} placeholder="Anything an approver should know" />
        </Form.Item>

        {!isEdit && (
          <Space direction="vertical" size={4} style={{ marginBottom: 8 }}>
            <Checkbox checked={submitNow} onChange={(e) => setSubmitNow(e.target.checked)}>
              Submit for approval straight away
            </Checkbox>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {voucherType === "income"
                ? "Income posts to the ledger as soon as it is created."
                : "Expenses post to the ledger only after enough approvals."}
            </span>
          </Space>
        )}
      </Form>

      <LedgerAccountModal
        open={accountModalOpen}
        accounts={accounts}
        quickType={voucherType}
        onClose={() => setAccountModalOpen(false)}
        onSaved={(account, created) => {
          if (created) {
            setAccounts((prev) => [...prev, account]);
            form.setFieldValue("account_id", account.id);
          }
        }}
      />
    </Modal>
  );
};

export default VoucherFormModal;
